import { Box, Chip, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { CaseStatusType, User } from '../../types';
import ExpandableText from '../common/ExpandableText';
import { CASE_STATE_OPTIONS, caseStateTone, compareStatusesNewestFirst, formatCaseState } from './caseState';

const setterLabel = (setter: CaseStatusType['setter']) => {
    if (!setter) return 'System';
    return (typeof setter === 'object' && (setter as User).full_name) || 'Unknown user';
};

const healerView = (status: CaseStatusType) => {
    const option = CASE_STATE_OPTIONS.find(item => item.value === status.state);
    if (!option) return null;
    if (!status.notes) return `Healer sees: ${option.healerSees}`;
    return `Healer sees: ${option.healerSees} · notes ${option.notesVisibleToHealer ? 'visible' : 'hidden'}`;
};

const StatusHistory = ({ statuses }: { statuses: CaseStatusType[] }) => {
    const entries = [...statuses].sort(compareStatusesNewestFirst);

    if (entries.length === 0) {
        return <Typography variant='body2' color='text.secondary'>No status history yet.</Typography>;
    }

    return (
        <Stack component='ol' spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label='Status history'>
            {entries.map((status, index) => (
                <Box component='li' key={status.id} sx={{ pl: 1.5, borderLeft: '3px solid', borderColor: index === 0 ? 'primary.main' : 'divider' }}>
                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                        <Chip size='small' label={formatCaseState(status.state)} color={caseStateTone(status.state)} variant='outlined' />
                        {index === 0 && <Chip size='small' label='Current' color='primary' />}
                    </Stack>
                    <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.5 }}>
                        {`${setterLabel(status.setter)} · ${DateTime.fromISO(status.created_date).toLocaleString(DateTime.DATETIME_MED)}`}
                    </Typography>
                    {healerView(status) && (
                        <Typography variant='caption' color='text.secondary' component='div'>{healerView(status)}</Typography>
                    )}
                    {status.notes && (
                        <Box sx={{ mt: 0.5 }}>
                            <ExpandableText text={status.notes} maxChars={200} />
                        </Box>
                    )}
                </Box>
            ))}
        </Stack>
    );
};

export default StatusHistory;
